import { Injectable } from "@angular/core";
import { BaseManager } from "./base.manager";
import { LocalStorageRepositoryInterface } from "../../datastore/local/localstorage.interface";

import { ListInfo } from "../../datastore/entities/listinfo.model";
import { NurseryModel } from "../../datastore/entities/nursery.model";
import { NurseryRepositoryInterface } from "../../datastore/repositories/nursery/nursery.interface";
import { Observable, of } from "rxjs";
import { catchError, concatMap, map } from "rxjs/operators";
import { NurseryRequest } from "../../crossconcern/types/nursery.type";
import { MediaRepositoryInterface } from "../../datastore/repositories/media/media.interface";
import { PolygonModel } from "../../datastore/entities/polygon.model";
import { PolygonType } from "../../crossconcern/types/polygon.type";
import { PolygonEdgeType } from "../../crossconcern/types/polygonedge.type";
import { ShortcutList } from "../../datastore/entities/shortcutlist.model";
import { ShortcutType } from "../../crossconcern/types/shortcut.type";
import { ShortcutModel } from "../../datastore/entities/shortcut.model";

@Injectable()
export class NurseryManager extends BaseManager {
    constructor(
        protected localStorageRepository: LocalStorageRepositoryInterface,
        private nurseryRepository: NurseryRepositoryInterface,
        private mediaRepository: MediaRepositoryInterface) {
        super(localStorageRepository);
    }

    public getNursery(uuid: string): Observable<NurseryModel> {
        return this.nurseryRepository.getNursery(uuid)
            .pipe(
                concatMap((nursery) => this.getNurseryMedia(nursery))
            );
    }

    public getNurseryMedia(nursery: NurseryModel, quality = "view_fhd"): Observable<NurseryModel> {
        return this.mediaRepository.getMediasByEntity("search=custom_tag:nursery_cover_image", nursery.uuid)
            .pipe(
                map(([
                    _, media
                ]) => {
                    if (media && media.length > 0) {
                        nursery.imageURL = media[0].getMediaURL(quality);
                    }
                    return nursery;
                }),
                catchError(() => {
                    return of(nursery);
                })
            );
    }

    public getNurseries(visible: boolean, pageSize: number, page: number): Observable<[ListInfo, NurseryModel[]]> {
        return this.nurseryRepository.getNurseries(visible, pageSize, page);
    }

    public editNursery(id: string, body: NurseryRequest): Observable<NurseryModel> {
        return this.nurseryRepository.editNursery(id, body).pipe();
    }

    public addNursery(body: unknown): Observable<NurseryModel> {
        return this.nurseryRepository.addNursery(body).pipe();
    }

    public deleteNursery(id: string): Observable<{}> {
        return this.nurseryRepository.deleteNursery(id).pipe();
    }

    public duplicateNursery(nurseryUUID: string): Observable<NurseryModel> {
        return this.nurseryRepository.duplicateNursery(nurseryUUID).pipe();
    }

    public addNurserySpheres(body: File[], nurseryUUID: string): Observable<{}> {
        const formData = new FormData();
        for(const file of body) {
            formData.append("files", file);
        }
        return this.nurseryRepository.addPhotospheres(formData, nurseryUUID);
    }

    public connectSpheres(nurseryUUID: string) {
        return this.nurseryRepository.connectPhotospheres(nurseryUUID);
    }

    public addNurseryCSV(body: File[], nurseryUUID: string): Observable<{}> {
        const formData = new FormData();
        for(const file of body) {
            formData.append("file", file);
        }
        return this.nurseryRepository.addCSV(formData, nurseryUUID);
    }

    /* eslint-disable camelcase, @typescript-eslint/camelcase */
    public getPolygons(pageSize: number, page: number, nurseryUUID?: string) {
        return this.nurseryRepository.getPolygons(pageSize, page, nurseryUUID);
    }

    public addPolygon(polygon, productUUID: string, nurseryUUID: string, productInfo: string)
        : Observable<PolygonModel> {
        const geofences: PolygonEdgeType[] = [];
        polygon.getPath().forEach((latLng, index) => {
            geofences.push({
                latitude: latLng.lat(),
                longitude: latLng.lng(),
                index
            });
        });
        const polyType: PolygonType = {
            geofences,
            nursery_uuid: nurseryUUID,
            product_id_cod: productUUID,
            product_info: productInfo
        };
        return this.nurseryRepository.addPolygon(polyType);
    }

    public editPolygon(polygonUUID: string, productUUID: string, productInfo: string) {
        const polygon: PolygonType = {
            product_id_cod: productUUID,
            nursery_uuid: undefined,
            geofences: undefined,
            product_info: productInfo
        };
        return this.nurseryRepository.editPolygon(polygonUUID, polygon);
    }

    public deletePolygon(polygonUUID: string) {
        return this.nurseryRepository.deletePolygon(polygonUUID);
    }

    public getShortcuts(pageSize: number, page: number, nurseryUUID?: string): Observable<ShortcutList> {
        return this.nurseryRepository.getShortcuts(pageSize, page, nurseryUUID);
    }

    public addShortcut(shortcut: ShortcutType): Observable<ShortcutModel> {
        return this.nurseryRepository.addShortcut(shortcut);
    }

    public editShortcut(shortcutUUID: string, shortcut: ShortcutType): Observable<ShortcutModel> {
        return this.nurseryRepository.editShortcut(shortcutUUID, shortcut);
    }

    public deleteShortcut(shortcutUUID: string): Observable<{}> {
        return this.nurseryRepository.deleteShortcut(shortcutUUID);
    }
}
